// src/components/assets/UploadProgressList.tsx

import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Chip,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useAddAsset } from '../../../context/AddAssetContext';

const UploadProgressList: React.FC = () => {
  const { filesToUpload, uploadProgress, allUploadsComplete, isUploading } = useAddAsset();

  return (
    // We removed the fixed height to let the parent container manage scrolling
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Upload Summary
      </Typography>

      {!isUploading && allUploadsComplete && (
         <Alert severity="success" sx={{ mb: 2 }}>
           All files have been uploaded successfully. Click "Save" to create the asset.
         </Alert>
      )}

      <List sx={{ width: '100%' }}>
        {filesToUpload.map((file, index) => {
          const progress = uploadProgress[file.name] || 0;
          const isComplete = progress === 100;
          const hasError = progress === -1;

          return (
            <ListItem key={index} divider sx={{ px: 0, py: 1.5, display: 'flex', flexDirection: 'column' }}>
              {/* --- TOP ROW: Filename and Status --- */}
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Box>
                  <Typography variant="body1" noWrap sx={{ maxWidth: '200px' }}>{file.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                  </Typography>
                </Box>
                
                {/* Status Chips/Text */}
                {isComplete && (
                  <Chip icon={<CheckCircleIcon />} label="Complete" color="success" size="small" />
                )}
                {hasError && (
                  <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
                )}
                {!isComplete && !hasError && (
                   <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>{`${Math.round(progress)}%`}</Typography>
                )}
              </Box>

              {/* --- BOTTOM ROW: Full-Width Progress Bar --- */}
              <LinearProgress
                variant="determinate"
                value={isComplete ? 100 : hasError ? 100 : progress}
                color={isComplete ? 'success' : hasError ? 'error' : 'primary'}
                sx={{ width: '100%', height: 8, borderRadius: 4 }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default UploadProgressList;